import React, { useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAdmin } from '../contexts/AdminContext';
import Button from '../components/Button';

const AdminContainer = styled.div`
  min-height: 80vh;
  padding: ${props => props.theme.spacing.xl} 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const AdminHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const AdminTitle = styled.h1`
  color: ${props => props.theme.colors.darkBlue};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const AdminSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.darkGray};
  max-width: 600px;
  margin: 0 auto;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  box-shadow: ${props => props.theme.shadows.md};
  border: 2px solid ${props => props.theme.colors.gray};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.darkBlue};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.darkGray};
  font-weight: 500;
`;

const AdminCard = styled.div`
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const CardTitle = styled.h2`
  color: ${props => props.theme.colors.darkBlue};
  margin-bottom: ${props => props.theme.spacing.lg};
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  margin-top: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
`;

const AdminPage: React.FC = () => {
  const { usageReports, exportToExcel } = useAdmin();

  const stats = useMemo(() => {
    const totalReports = usageReports.length;
    const maleReports = usageReports.filter(r => r.gender === 'male').length;
    const femaleReports = usageReports.filter(r => r.gender === 'female').length;
    const avgPension = usageReports.length > 0 
      ? usageReports.reduce((sum, r) => sum + r.actualPension, 0) / usageReports.length 
      : 0;
    const avgExpectedPension = usageReports.length > 0 
      ? usageReports.reduce((sum, r) => sum + r.expectedPension, 0) / usageReports.length 
      : 0;

    return {
      totalReports,
      maleReports,
      femaleReports,
      avgPension: Math.round(avgPension),
      avgExpectedPension: Math.round(avgExpectedPension)
    };
  }, [usageReports]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <AdminContainer>
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <AdminHeader>
            <motion.div variants={itemVariants}>
              <AdminTitle>Panel Administracyjny</AdminTitle>
              <AdminSubtitle>
                Zarządzanie danymi i raportowanie użycia ZUSik
              </AdminSubtitle>
            </motion.div>
          </AdminHeader>

          <motion.div variants={itemVariants}>
            <StatsGrid>
              <StatCard>
                <StatValue>{stats.totalReports}</StatValue>
                <StatLabel>Łączna liczba symulacji</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.maleReports}</StatValue>
                <StatLabel>Symulacje mężczyzn</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.femaleReports}</StatValue>
                <StatLabel>Symulacje kobiet</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.avgPension.toLocaleString('pl-PL')} PLN</StatValue>
                <StatLabel>Średnia emerytura</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.avgExpectedPension.toLocaleString('pl-PL')} PLN</StatValue>
                <StatLabel>Średnia oczekiwana emerytura</StatLabel>
              </StatCard>
            </StatsGrid>
          </motion.div>

          <motion.div variants={itemVariants}>
            <AdminCard>
              <CardTitle>Raport użycia</CardTitle>
              <p style={{ textAlign: 'center', marginBottom: '2rem' }}>
                Panel administracyjny umożliwia przeglądanie statystyk użycia ZUSik 
                i eksportowanie danych do plików Excel.
              </p>
              
              <ButtonGroup>
                <Button
                  variant="outline"
                  onClick={() => window.history.back()}
                >
                  Wróć
                </Button>
                <Button
                  variant="primary"
                  onClick={exportToExcel}
                  disabled={usageReports.length === 0}
                >
                  Eksportuj do Excel
                </Button>
              </ButtonGroup>
            </AdminCard>
          </motion.div>
        </motion.div>
      </Container>
    </AdminContainer>
  );
};

export default AdminPage;
